/**
 * What a share row is worth at each market date visited in this edit, and the weight that is of the
 * AUM, for the share count and the AUM it was valued at. A share row's value and weight are always
 * Cold War's — a sizing by its own shares at the day (`./sizing`) — never quantity × price or value ÷
 * AUM on the frontend: Cold War knows how each instrument is quoted (a bond in % of nominal) and how
 * many decimals a weight carries, and the row may not. A value is reused only while the row holds the
 * shares and the AUM it was given for; a row whose share count or AUM changed since is valued again.
 * Nothing here calls the API or touches the builder.
 */
import type { Instrument } from "../../const";

/** Cold War's market value and weight of the row at a day, and the share count and AUM it valued them for. */
export type ValueAtDate = { shares: number; aum: number; marketValue: number; weight: number };

/**
 * What valuing a row adds to it. One named block for the same reason as the other row states in
 * `../../const`: dropping the feature drops this type, and the compiler names every reader.
 */
export type InstrumentValueByDateAttributes = {
	valueByDate?: Readonly<Record<string, ValueAtDate>>;
};

/** The row's value and weight at `date`, when it was given them there for the shares it holds now and `aum`. */
export function rememberedValue(
	row: Instrument,
	date: string,
	aum: number,
): Pick<ValueAtDate, "marketValue" | "weight"> | undefined {
	const remembered = row.valueByDate?.[date];
	if (remembered == null || remembered.shares !== row.quantity || remembered.aum !== aum) {
		return undefined;
	}
	return { marketValue: remembered.marketValue, weight: remembered.weight };
}

/** The row's memo with `value` written under `date`. */
export function withRememberedValue(row: Instrument, date: string, value: ValueAtDate): Instrument["valueByDate"] {
	return { ...row.valueByDate, [date]: value };
}

/**
 * A row as it was loaded, with the value and weight it arrived carrying remembered under `date` for
 * `aum`: the backend valued it at its latest close, which is the day the editor opens on
 * (`../market-date/pricing`), against the AUM the editor opens with.
 */
export function rememberLoadedValue(row: Instrument, date: string, aum: number): Instrument {
	if (
		row.isCash ||
		row.heldByValue ||
		row.quantity == null ||
		row.marketValue == null ||
		row.weight == null ||
		row.valueByDate?.[date]
	) {
		return row;
	}
	return {
		...row,
		valueByDate: withRememberedValue(row, date, {
			shares: row.quantity,
			aum,
			marketValue: row.marketValue,
			weight: row.weight,
		}),
	};
}
